import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useOrderDetailsFilterQuery, usePack } from "@queries/order";
import { useTimeSlotsQuery } from "@queries/time_slots";
import { getOrderWorkflowTimeSlot, getServiceWindow } from "../../lib/orderSort";
import "@styles/opencart-admin.css";

const ORDER_TYPES = [
  "car_cleaning",
  "normal",
  "skip",
  "express",
  "complementary",
  "disinfection",
  "furniture",
  "housekeeping",
  "carpets",
  "chemicals",
];

const DEFAULT_SELECTED_TYPES = ["skip", "normal", "express"];

const todayISO = () => new Date().toISOString().slice(0, 10);

function labelForType(t) {
  if (t.includes(",")) {
    return t.split(",").map(labelForType).join(" & ");
  }
  if (t === "car_cleaning") return "Car Cleaning";
  if (t === "express") return "Express Orders";
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Other";
}

function printTableOnly(content, title) {
  if (!content) return;

  const w = window.open("", "_blank", "width=1100,height=750");
  if (!w) return;

  const contentHtml = typeof content === 'string' ? content : content.outerHTML;

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${String(title || "Print")}</title>
  <style>
    html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
    .page { padding: 18px; }
    .hdr { margin-bottom: 12px; }
    .hdr h2 { margin: 0 0 6px 0; font-size: 16px; }
    .hdr .meta { font-size: 12px; color: #444; }

    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th, td { border: 1px solid #cfcfcf; padding: 6px 8px; font-size: 12px; vertical-align: top; }
    th { font-weight: 700; }
    td { word-break: break-word; }

    /* Hide Action column (last column) */
    thead tr > th:last-child,
    tbody tr > td:last-child { display: none !important; }
    button, .no-print { display: none !important; }
    .print-only-heading { display: block !important; margin-top: 20px; margin-bottom: 10px; font-size: 18px; font-weight: bold; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      a { color: inherit; text-decoration: none; }
      .page { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="hdr">
      <h2>${String(title || "Orders")}</h2>
      <div class="meta">Printed: ${new Date().toLocaleString()}</div>
    </div>
    ${contentHtml}
  </div>
</body>
</html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();

  w.onload = () => {
    w.focus();
    w.print();
  };

  w.onafterprint = () => {
    w.close();
  };
}

export default function CleaningOrdersTwoDays() {
  const [search, setSearch] = useSearchParams();
  const dayParam = search.get("day") || todayISO();
  const day = dayParam;
  const [localDay, setLocalDay] = React.useState(day);

  const { data: timeSlots = [] } = useTimeSlotsQuery(day);

  React.useEffect(() => {
    setLocalDay(day);
  }, [day]);

  const handleDateChange = (e) => {
    const v = e.target.value;
    setLocalDay(v);
    const sp = new URLSearchParams(search);
    if (v) sp.set("day", v);
    else sp.delete("day");
    setSearch(sp);
  };
  const day2 = React.useMemo(() => {
    const d = new Date(day + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  }, [day]);

  const handlePrintAll = () => {
    const sections = document.querySelectorAll('.day-section-print');
    let html = "";
    sections.forEach(sec => {
      html += sec.innerHTML;
    });
    printTableOnly(html, "Cleaning Orders (Two Days)");
  };

  return (
    <div className="oc-content">
      <div className="oc-header">
        <h1>Cleaning Orders (Two Days)</h1>
      </div>

      <div className="oc-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <strong style={{ marginRight: 8 }}>Date:</strong>
            <input type="date" className="form-control" value={localDay} onChange={handleDateChange} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="oc-btn ghost" onClick={handlePrintAll}>Print All Page Tables</button>
            <a href="/" className="oc-btn">Back</a>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        <DaySection key={`day1-${day}`} day={day} timeSlots={timeSlots} />
        <DaySection key={`day2-${day2}`} day={day2} timeSlots={timeSlots} />
      </div>
    </div>
  );
}

function DaySection({ day, timeSlots }) {
  const [selectedTypes, setSelectedTypes] = React.useState(DEFAULT_SELECTED_TYPES);
  const [flaggedItems, setFlaggedItems] = React.useState(false);
  const [sewingItems, setSewingItems] = React.useState(false);
  const [packFilter, setPackFilter] = React.useState("unpacked");
  const [selectedSlots, setSelectedSlots] = React.useState([]);
  const [appliedSlots, setAppliedSlots] = React.useState([]);

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const q = React.useMemo(
    () => ({
      dropoff_date: day,
      time_slot: appliedSlots.length ? appliedSlots : undefined,
      order_type: selectedTypes.length ? selectedTypes : undefined,
      packed: packFilter === "packed" ? 1 : packFilter === "unpacked" ? 0 : undefined,
      flagged_items: flaggedItems ? 1 : undefined,
      sewing_items: sewingItems ? 1 : undefined,
      lang: 1,
      with_items: 1,
      order_status_id: 17,
      page: 1,
      page_size: 1000,
    }),
    [day, appliedSlots, selectedTypes, packFilter, flaggedItems, sewingItems]
  );

  const { data: resp } = useOrderDetailsFilterQuery(q);
  const rows = resp?.data ?? [];

  const isOrderUnpacked = (r) => {
    if (r.items_count && typeof r.items_count === 'object') {
      const values = Array.isArray(r.items_count) ? r.items_count : Object.values(r.items_count);
      if (values.length > 0) {
        for (const item of values) {
          if (Number(item?.not_packed_count) > 0) {
            return true;
          }
        }
        return false;
      }
    }
    return !r?.order?.packed;
  };

  const filteredRows = React.useMemo(() => {
    return rows.filter(r => {
      if (packFilter === "all") return true;
      const unpacked = isOrderUnpacked(r);
      if (packFilter === "packed") return !unpacked;
      if (packFilter === "unpacked") return unpacked;
      return true;
    });
  }, [rows, packFilter]);

  const visibleRows = React.useMemo(() => {
    return filteredRows.filter(r => selectedTypes.includes(r.type || "other"));
  }, [filteredRows, selectedTypes]);

  const ordersNumber = visibleRows.length;
  let itemsNumber = 0;
  let unpackedNumber = 0;
  let flaggedNumber = 0;
  let sewingNumber = 0;

  for (const r of visibleRows) {
    if (r.items_count && typeof r.items_count === 'object') {
      const values = Array.isArray(r.items_count) ? r.items_count : Object.values(r.items_count);
      if (values.length > 0) {
        let orderItems = 0;
        let orderUnpacked = 0;
        for (const item of values) {
          orderItems += Number(item?.item_count) || 0;
          orderUnpacked += Number(item?.not_packed_count) || 0;
        }
        if (orderUnpacked > orderItems) {
          orderItems = orderUnpacked;
        }
        itemsNumber += orderItems;
        unpackedNumber += orderUnpacked;
      } else {
        const count = Number(r.count || (r.items ? r.items.length : 0)) || 0;
        itemsNumber += count;
        if (!r?.order?.packed) {
          unpackedNumber += count;
        }
      }
    } else {
      const count = Number(r.items_count || r.count || (r.items ? r.items.length : 0)) || 0;
      itemsNumber += count;
      if (!r?.order?.packed) {
        unpackedNumber += count;
      }
    }
    flaggedNumber += Number(r.flagged_items_count) || 0;
    sewingNumber += Number(r.sewing_items_count) || 0;
  }

  const packedNumber = itemsNumber - unpackedNumber;

  const tableRef = React.useRef(null);

  const onPrintAll = () => {
    const title = `Orders • Date: ${day} • Count: ${visibleRows.length}`;
    printTableOnly(tableRef.current, title);
  };

  return (
    <div className="oc-card" style={{ padding: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <strong>{`Date: ${day}`}</strong>
          <div style={{ fontSize: 13, color: "#444" }}>
            {`orders number:${ordersNumber} with (${itemsNumber}) items | UnPacked:${unpackedNumber} | Packed:${packedNumber} | Flagged:${flaggedNumber} | Sewing:${sewingNumber}`}
          </div>
          <div style={{ marginTop: 8 }}>
            <button className="oc-btn ghost" onClick={onPrintAll}>Print Table</button>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div style={{ display: 'flex', flexDirection: 'row', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        
        {/* Order Types */}
        <div style={{ minWidth: 'fit-content' }}>
          <strong style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>Order Type</strong>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, background: '#f8f8f8', padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd' }}>
            {ORDER_TYPES.map((t) => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 13, userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(t)}
                  onChange={() => toggleType(t)}
                />
                {labelForType(t)}
              </label>
            ))}
          </div>
        </div>
        
        {/* Flagged/Sewing */}
        <div style={{ minWidth: 'fit-content' }}>
          <strong style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>Order Flags</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <label
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13,
                padding: '6px 12px', borderRadius: 6,
                border: flaggedItems ? '1.5px solid #d9534f' : '1px solid #ddd',
                background: flaggedItems ? '#fbeaea' : '#fff',
                color: flaggedItems ? '#b52a2a' : '#333',
                fontWeight: flaggedItems ? 600 : 400,
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={flaggedItems}
                onChange={(e) => setFlaggedItems(e.target.checked)}
                style={{ display: 'none' }}
              />
              <span>🚩</span> Flagged
            </label>
            <label
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13,
                padding: '6px 12px', borderRadius: 6,
                border: sewingItems ? '1.5px solid #5cb85c' : '1px solid #ddd',
                background: sewingItems ? '#eaf6ea' : '#fff',
                color: sewingItems ? '#2d6e2d' : '#333',
                fontWeight: sewingItems ? 600 : 400,
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={sewingItems}
                onChange={(e) => setSewingItems(e.target.checked)}
                style={{ display: 'none' }}
              />
              <span>🧵</span> Sewing
            </label>
          </div>
        </div>

        {/* Time Slot */}
        <div style={{ minWidth: 220, flex: 1, maxWidth: 300 }}>
          <strong style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>Time Slot</strong>
          <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', maxHeight: 80, overflowY: 'auto', background: '#fff' }}>
            {(timeSlots || []).map((t) => {
              const val = String(t?.value ?? t);
              const lbl = t?.label ?? t;
              const checked = selectedSlots.includes(val);
              return (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, padding: '2px 0' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setSelectedSlots(prev => checked ? prev.filter(s => s !== val) : [...prev, val])}
                  />
                  {lbl}
                </label>
              );
            })}
          </div>
        </div>

        {/* Pack Filter */}
        <div style={{ minWidth: 140 }}>
          <strong style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>Pack Filter</strong>
          <select
            value={packFilter}
            onChange={(e) => setPackFilter(e.target.value)}
            className="form-control"
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', width: '100%', fontSize: 13 }}
          >
            <option value="all">All</option>
            <option value="packed">Packed</option>
            <option value="unpacked">Unpacked</option>
          </select>
        </div>

        {/* Apply Button */}
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 26 }}>
          <button 
            className="oc-btn" 
            onClick={() => setAppliedSlots(selectedSlots)}
            style={{ height: '34px', padding: '0 20px', backgroundColor: '#337ab7', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Apply Time Slots
          </button>
        </div>

      </div>

      {visibleRows.length === 0 && <div style={{ marginTop: 12 }}>No orders</div>}

      <div className="day-section-print">
        <div style={{ display: 'none' }} className="print-only-heading">Date: {day}</div>
        <div style={{ marginTop: 12 }} ref={tableRef}>
          <OrdersTable rows={visibleRows} />
        </div>
      </div>
    </div>
  );
}

function OrderRow({ r }) {
  const pack = usePack(r.order_id);
  const bg = r.type === "express" ? "gold" : undefined;

  return (
    <tr style={bg ? { backgroundColor: bg } : undefined}>
      <td style={{ width: 120 }}>
        <button className="oc-btn" onClick={() => pack.mutate()} disabled={pack.isLoading}>
          Pack
        </button>
        <div style={{ marginTop: 6 }}>{r.order_id}</div>
      </td>
      <td style={{ minWidth: 250 }}>
        {r.items_display ? (
          <div dangerouslySetInnerHTML={{ __html: r.items_display }} />
        ) : Array.isArray(r.items) ? (
          r.items.map((it, i) => <div key={i} dangerouslySetInnerHTML={{ __html: it.name ?? it }} />)
        ) : r.items_count && typeof r.items_count === 'object' ? (
          (() => {
            const vals = Object.values(r.items_count);
            const items = vals.reduce((sum, item) => sum + (Number(item?.item_count) || 0), 0);
            const unpacked = vals.reduce((sum, item) => sum + (Number(item?.not_packed_count) || 0), 0);
            return Math.max(items, unpacked) || r.count || "-";
          })()
        ) : (
          r.items_count || r.count || "-"
        )}
      </td>
      <td style={{ width: 200 }}>{getOrderWorkflowTimeSlot(r) || r.time_slot || getServiceWindow(r) || "-"}</td>
      <td style={{ width: 200 }}>{r.customer || r.firstname || "-"}</td>
      <td style={{ whiteSpace: "normal", minWidth: 250 }} dangerouslySetInnerHTML={{ __html: r.notes_display || "-" }} />
      <td style={{ width: 120 }}>{r.first_order ? "Yes" : "No"}</td>
      <td style={{ width: 120 }}>
        <Link className="oc-btn ghost" to={`/orders/${r.order_id}`}>
          view
        </Link>
      </td>
    </tr>
  );
}

function OrdersTable({ rows }) {
  return (
    <div className="table-wrap" style={{ overflowX: "auto" }}>
      <table className="notes-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Items</th>
            <th>Time Slot</th>
            <th>Customer</th>
            <th>Notes</th>
            <th>1st</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <OrderRow key={r.order_id} r={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
